/**
 * notificationPrefsStore.ts — Preferencias de notificaciones por categoría (Zustand)
 *
 * QUÉ HACE:
 * - Persiste qué categorías (ventas, inventario, personal, sistema) quiere
 *   recibir el usuario en este dispositivo — mismo patrón que useThemeStore.
 * - Al activar una categoría por primera vez, solicita el permiso real de
 *   notificaciones del sistema operativo (Android/iOS/Escritorio/Web).
 * - Expone `isNotificationCategoryEnabled` para que otros módulos (ej.
 *   auditoría) sepan si deben disparar una notificación local sin usar hooks.
 *
 * QUÉ EXPORTA: useNotificationPrefsStore, isNotificationCategoryEnabled
 *
 * QUÉ NO DEBE HACER:
 * - No debe armar el payload de la notificación (eso vive en lib/push).
 * - No debe acceder a Capacitor/Tauri directamente (usa @/lib/push, que a su
 *   vez delega en @/lib/platform).
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  requestMobileNotificationPermission,
  ensureNotificationChannels,
  ensureNotificationActionTypes,
} from "@/lib/push";
import type { NotificationCategory } from "@/lib/push";

/** Categorías configurables desde Ajustes (excluye "marketing", sin UI aún). */
export type SettingsNotificationCategory = Exclude<
  NotificationCategory,
  "marketing"
>;

interface NotificationPrefsState {
  prefs: Record<SettingsNotificationCategory, boolean>;
  /** true si el usuario ya concedió el permiso del SO al menos una vez. */
  permissionGranted: boolean;
  /**
   * Activa/desactiva una categoría. Al activar pide permiso del SO; si lo
   * niega, la categoría queda apagada. Retorna el estado final (true = ON).
   */
  toggleCategory: (key: SettingsNotificationCategory) => Promise<boolean>;
}

export const useNotificationPrefsStore = create<NotificationPrefsState>()(
  persist(
    (set, get) => ({
      prefs: {
        ventas: true,
        inventario: true,
        personal: false,
        sistema: true,
      },
      permissionGranted: false,

      toggleCategory: async (key) => {
        const turningOn = !get().prefs[key];

        if (!turningOn) {
          set((state) => ({ prefs: { ...state.prefs, [key]: false } }));
          return false;
        }

        const granted = await requestMobileNotificationPermission();
        if (granted) {
          // Crea/actualiza los 4 canales Android (Ventas/Inventario/Personal/
          // Sistema) y registra la acción rápida "Marcar como leído".
          await ensureNotificationChannels();
          await ensureNotificationActionTypes();
        }
        set((state) => ({
          permissionGranted: granted || state.permissionGranted,
          prefs: { ...state.prefs, [key]: granted },
        }));
        return granted;
      },
    }),
    { name: "altenot-notification-prefs" },
  ),
);

/** Helper sin hooks para usar fuera de componentes React (ej. services). */
export function isNotificationCategoryEnabled(
  category: SettingsNotificationCategory,
): boolean {
  return useNotificationPrefsStore.getState().prefs[category];
}
